// (c) chaehongee. Confidential and proprietary.

import type { ChatMessage } from './types'
import type { MessageResponse } from 'models-ts/message'

export function stripGenerationParams(text: string): string {
    if (!text) return ''
    return text
        .replace(/<generation_params>[\s\S]*?<\/generation_params>/g, '')
        .replace(/<camera_preset>[\s\S]*?<\/camera_preset>/g, '')
        .trim()
}

// Helper to parse inner message content (JSON or text) from string
function parseInnerContent(raw: string): string | null {
    if (!raw) return null

    // Check for <message> tags
    const match = raw.match(/<message>\n([\s\S]*?)\n<\/message>/)
    const content = match ? (match[1]?.trim() ?? '') : raw

    // Try parsing as JSON
    try {
        const parsed = JSON.parse(content)
        if (typeof parsed?.text === 'string') {
            return stripGenerationParams(parsed.text)
        }
    } catch {
        // Not valid JSON, continue
    }

    // Check for python-dict style text field
    const textMatch = content.match(/'text'\s*:\s*'([\s\S]*?)'/)
    if (textMatch) {
        return stripGenerationParams(textMatch[1])
    }

    // Fallback to raw content if we stripped tags
    return match ? stripGenerationParams(content) : stripGenerationParams(raw)
}

export function extractUserMessageTextFromStored(content: unknown): string {
    // Handle array of blocks
    if (Array.isArray(content)) {
        const candidates = content
            .filter(b => b && typeof b === 'object' && b.type === 'text' && typeof b.text === 'string')
            .map(b => parseInnerContent(b.text))
            .filter(Boolean) as string[]

        return candidates.length > 0 ? candidates[candidates.length - 1] : ''
    }

    // Handle simple string
    if (typeof content === 'string') {
        return parseInnerContent(content) || ''
    }

    return ''
}

export function parseAssistantBlocks(content: unknown, baseId: string, baseTimestamp: number): ChatMessage[] {
    // Handle simple string
    if (typeof content === 'string' && content) {
        return [{
            kind: 'bubble',
            role: 'assistant',
            text: content,
            id: baseId,
            timestamp: baseTimestamp
        }]
    }

    if (!Array.isArray(content)) return []

    const rows: ChatMessage[] = []
    let currentText = ''
    let index = 0

    for (const block of content) {
        if (!block || typeof block !== 'object') continue

        const { type, text, name } = block

        if (type === 'text' && typeof text === 'string') {
            currentText += text
            continue
        }

        if (type === 'tool_use') {
            if (currentText) {
                rows.push({
                    kind: 'bubble',
                    role: 'assistant',
                    text: currentText,
                    id: `${baseId}-text-${index}`,
                    timestamp: baseTimestamp
                })
                currentText = ''
            }

            const toolKey = typeof name === 'string' ? name : 'tool'
            rows.push({
                kind: 'note',
                // Store the raw tool key; the thread renders a human action
                // label + quiet model attribution from it (see ToolNote).
                text: `calling tool: ${toolKey}`,
                id: `${baseId}-tool-${index}`,
                timestamp: baseTimestamp
            })
            index++
        }
    }

    if (currentText) {
        rows.push({
            kind: 'bubble',
            role: 'assistant',
            text: currentText,
            id: `${baseId}-text-final`,
            timestamp: baseTimestamp
        })
    }

    return rows
}

export function parseSessionMessages(messages: MessageResponse[] | undefined): ChatMessage[] {
    if (!messages) return []

    const rows: ChatMessage[] = []

    for (const m of messages) {
        const timestamp = new Date(m.created_at).getTime()

        if (m?.role === 'user') {
            const explicitText = (typeof m?.text === 'string' && m.text) ? m.text : ''
            const text = stripGenerationParams(explicitText) || extractUserMessageTextFromStored(m?.content)

            if (text) {
                rows.push({
                    kind: 'bubble',
                    role: 'user',
                    text,
                    id: m.id,
                    timestamp,
                    nodeIds: m.referenced_node_ids || []
                })
            }
        } else {
            const assistantRows = parseAssistantBlocks(m?.content, m.id, timestamp)
            // Attach metadata to bubble messages
            for (const row of assistantRows) {
                if (row.kind === 'bubble') {
                    row.nodeIds = m.referenced_node_ids || []
                }
                rows.push(row)
            }
        }
    }
    return rows
}
